import React, { useEffect, useMemo, useRef, useState } from "react";

import { getOverviewMetricsRangeSpanDays } from "../metricsHelpers";
import {
  createOverviewChartPanPlugin,
  getDefaultOverviewChartGranularity,
  getTodayLineAnnotation,
} from "./chartsHelpers";

/** Horizontal pan distance (px) when the date window slides. */
const CHART_SHIFT_PAN_PX = 36;
/** Pan animation duration (ms). */
const CHART_SHIFT_PAN_MS = 220;

/**
 * Shared shell for Project Overview metric charts (loading/empty + pan on range slide).
 *
 * Chart.js height tweens are disabled — they fight the sideways pan and make bars
 * “regrow” on every date move. Motion comes only from the plot-area pan plugin.
 *
 * @param {Object} props
 * @param {Array<Object>} [props.days] - Aggregated day rows for the active filters.
 * @param {{key?: string, start: string|null, end: string|null}|null} [props.range] - Active Range filter.
 * @param {"daily"|"weekly"} [props.granularity] - Controlled bucket mode from the filter row.
 * @param {string} [props.typeLabel="All"] - Active Type name for chart dataset labels.
 * @param {boolean} [props.isLoading] - Whether source metrics are still loading.
 * @param {string} props.emptyMessage - Message when the filtered range has no data points.
 * @param {(days: Array, range: Object|null, granularity: string, typeLabel: string) => Object} props.buildChartData
 * @param {() => Object} props.getChartOptions - Chart.js options factory.
 * @param {(data: Object, options: Object) => React.ReactNode} props.renderChart - Renders the Chart.js instance.
 * @returns {JSX.Element}
 */
const OverviewMetricsChart = ({
  days = [],
  range = null,
  granularity: granularityProp,
  typeLabel = "All",
  isLoading = false,
  emptyMessage,
  buildChartData,
  getChartOptions,
  renderChart,
}) => {
  const [fallbackGranularity, setFallbackGranularity] = useState(() =>
    getDefaultOverviewChartGranularity(range, days)
  );
  const granularity = granularityProp ?? fallbackGranularity;

  const chartRef = useRef(null);
  const panOffsetRef = useRef(0);
  const panRafRef = useRef(null);
  const prevRangeStartRef = useRef(range?.start);
  const prevRangeSpanRef = useRef(getOverviewMetricsRangeSpanDays(range));

  const panPlugin = useMemo(
    () => createOverviewChartPanPlugin(() => panOffsetRef.current),
    []
  );

  const rangeSpanDays = getOverviewMetricsRangeSpanDays(range);
  useEffect(() => {
    if (granularityProp != null) return;
    setFallbackGranularity(getDefaultOverviewChartGranularity(range, days));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granularityProp, rangeSpanDays, days]);

  // Pan plot datasets when start/end slide with the same span (range chevrons).
  useEffect(() => {
    const prevStart = prevRangeStartRef.current;
    const prevSpan = prevRangeSpanRef.current;
    const nextStart = range?.start;
    prevRangeStartRef.current = nextStart;
    prevRangeSpanRef.current = rangeSpanDays;

    if (
      !prevStart ||
      !nextStart ||
      prevStart === nextStart ||
      prevSpan == null ||
      rangeSpanDays == null ||
      prevSpan !== rangeSpanDays
    ) {
      return;
    }

    const direction = nextStart > prevStart ? 1 : -1;

    if (panRafRef.current != null) {
      window.cancelAnimationFrame(panRafRef.current);
      panRafRef.current = null;
    }

    const fromX = direction === -1 ? -CHART_SHIFT_PAN_PX : CHART_SHIFT_PAN_PX;
    const startedAt = performance.now();
    panOffsetRef.current = fromX;
    chartRef.current?.update("none");

    /**
     * Ease the dataset offset back to 0 and redraw without Chart.js tweens.
     *
     * @param {number} now
     * @returns {void}
     */
    const tick = (now) => {
      const t = Math.min(1, (now - startedAt) / CHART_SHIFT_PAN_MS);
      const eased = 1 - (1 - t) * (1 - t);
      panOffsetRef.current = fromX * (1 - eased);
      chartRef.current?.update("none");
      if (t < 1) {
        panRafRef.current = window.requestAnimationFrame(tick);
        return;
      }
      panOffsetRef.current = 0;
      panRafRef.current = null;
      chartRef.current?.update("none");
    };

    panRafRef.current = window.requestAnimationFrame(tick);
  }, [range?.start, range?.end, rangeSpanDays]);

  useEffect(
    () => () => {
      if (panRafRef.current != null) {
        window.cancelAnimationFrame(panRafRef.current);
      }
    },
    []
  );

  const data = useMemo(
    () => buildChartData(days, range, granularity, typeLabel),
    [buildChartData, days, range, granularity, typeLabel]
  );

  const options = useMemo(() => {
    const base = getChartOptions();
    return {
      ...base,
      // Always off: react-chartjs-2 updates run before any slide suppress state,
      // so height tweens otherwise “regrow” bars on every chevron move.
      animation: false,
      animations: false,
      transitions: {
        ...base.transitions,
        active: { animation: { duration: 0 } },
        resize: { animation: { duration: 0 } },
        show: { animations: { colors: false, x: false, y: false } },
        hide: { animations: { colors: false, x: false, y: false } },
      },
      plugins: {
        ...base.plugins,
        annotation: getTodayLineAnnotation(data.categoryKeys, data.weekly),
      },
    };
  }, [getChartOptions, data.categoryKeys, data.weekly]);

  const hasPoints = data.labels.length > 0;

  const chartNode = hasPoints ? renderChart(data, options) : null;
  const chartWithPan =
    chartNode && React.isValidElement(chartNode)
      ? React.cloneElement(chartNode, {
          ref: chartRef,
          plugins: [panPlugin, ...(chartNode.props.plugins || [])],
        })
      : chartNode;

  return (
    <div className="mt-2 rounded-xl border border-gray-200 bg-white px-2 py-2">
      <div className="h-[200px]">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-500">
            Loading chart…
          </div>
        ) : !hasPoints ? (
          <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-500">
            {emptyMessage}
          </div>
        ) : (
          chartWithPan
        )}
      </div>
    </div>
  );
};

export default OverviewMetricsChart;
